/**
 * Imposta, cambia o toglie la scadenza di un file.
 *
 * La scadenza dice entro quando il file va sostituito con una versione
 * aggiornata. **Non e' la durata di pubblicazione**: passata la data il file
 * resta online e nasce l'allerta «File da sostituire»; per farlo uscire dalla
 * pubblicazione c'e' «Durata…». La modale lo dice, perche' le due date stanno
 * una accanto all'altra e si confondono facilmente.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { ErroreApi, impostaScadenzaDocumento } from "../api/client";
import type { Documento } from "../api/types";
import { testoCopie } from "../lib/copie";
import { leggiScadenzaFile } from "../lib/scadenze";
import { CampoDato } from "./campi";
import { Modale } from "./Modale";
import { Avviso, Bottone } from "./primitive";

export function ModaleScadenza({
  documento,
  contesto,
  onChiudi,
  onCompletato,
}: {
  documento: Pick<Documento, "id" | "titolo" | "valido_fino_al" | "copie_collegate">;
  contesto?: ReactNode;
  onChiudi: () => void;
  onCompletato: (aggiornato: Documento) => void;
}) {
  const iniziale = documento.valido_fino_al ?? "";
  const [testo, setTesto] = useState(iniziale);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const letta = leggiScadenzaFile(testo);
  const invariata = testo.trim() === iniziale;

  const salva = async () => {
    if (letta.errore || invariata) return;
    setErrore(null);
    setInCorso(true);
    try {
      onCompletato(await impostaScadenzaDocumento(documento.id, letta.valore));
    } catch (causa) {
      setErrore(causa instanceof ErroreApi ? causa.messaggioEsteso : "Scadenza non salvata.");
    } finally {
      setInCorso(false);
    }
  };

  const chiudi = () => {
    if (!inCorso) onChiudi();
  };

  return (
    <Modale
      titolo={`Scadenza di «${documento.titolo}»`}
      aperta
      onChiudi={chiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={chiudi} disabled={inCorso}>
            Annulla
          </Bottone>
          <Bottone
            variante="primario"
            inCorso={inCorso}
            disabled={Boolean(letta.errore) || invariata}
            onClick={() => void salva()}
          >
            {letta.valore === null && iniziale ? "Togli la scadenza" : "Salva la scadenza"}
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {contesto ? <div>{contesto}</div> : null}
        {errore ? (
          <Avviso tono="critico" titolo="Scadenza non salvata">
            {errore}
          </Avviso>
        ) : null}
        <CampoDato
          etichetta="Da sostituire entro il"
          tipo="data"
          valore={testo}
          onCambia={setTesto}
          errore={testo.trim() ? letta.errore : null}
          aiuto="Vuoto: nessuna scadenza."
        />
        {documento.copie_collegate?.length ? (
          // La scadenza e' del file, e le copie sono lo stesso file: cambia
          // anche li'. Dirlo prima evita di scoprirlo da un'allerta.
          <p className="campo__aiuto">
            Vale anche per le copie collegate: {testoCopie(documento.copie_collegate)}.
          </p>
        ) : null}
        <p className="campo__aiuto">
          Passata la data il file resta pubblicato e compare fra le allerte, da sostituire. Per
          farlo uscire dalla pubblicazione si usa «Durata…».
        </p>
      </div>
    </Modale>
  );
}
